import api from './client';
import type {
  Program,
  Phase,
  Task,
  Enrollment,
  Certificate,
  TaskCompletion,
  PhaseCompletion,
  ReviewQueueItem,
} from '../../pages/training-programs/types';

// Program with full phase/task tree (returned by getProgram detail endpoint)
export type ProgramDetail = Program & { phases: Phase[] };

// Enrollment progress response shape
interface EnrollmentProgressResponse {
  enrollment: Enrollment;
  tasks: TaskCompletion[];
  phases: PhaseCompletion[];
}

// Training Programs API
export const trainingAPI = {
  // Programs
  getPrograms: (params?: {
    category?: string;
    difficulty?: string;
    limit?: number;
    offset?: number;
  }) => api.get<{ data: { programs: Program[]; total: number } }>('/training/programs', { params }),
  getProgram: (programId: number) => api.get<{ data: ProgramDetail }>(`/training/programs/${programId}`),
  createProgram: (data: {
    name: string;
    description?: string;
    category?: 'foundations' | 'bondage' | 'discipline' | 'service' | 'protocol' | 'custom';
    difficultyLevel?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
    estimatedDurationDays?: number;
    isTemplate?: boolean;
    isPublic?: boolean;
    prerequisites?: string[];
    learningObjectives?: string[];
  }) => api.post<{ data: Program }>('/training/programs', data),
  updateProgram: (programId: number, data: Partial<{
    name: string;
    description: string;
    category: string;
    difficultyLevel: string;
    estimatedDurationDays: number;
    isTemplate: boolean;
    isPublic: boolean;
    prerequisites: string[];
    learningObjectives: string[];
  }>) => api.patch<{ data: Program }>(`/training/programs/${programId}`, data),
  deleteProgram: (programId: number) => api.delete<{ message: string }>(`/training/programs/${programId}`),
  cloneProgram: (programId: number, name?: string) =>
    api.post<{ data: Program }>(`/training/programs/${programId}/clone`, { name }),

  // Program Enrollments
  getProgramEnrollments: (programId: number) =>
    api.get<{ data: Enrollment[] }>(`/training/programs/${programId}/enrollments`),
  // Spec 243 D4: enrollmentSource tags which of the three surfacing hook
  // points (onboarding_step / getting_started_card / dismissible_popup)
  // drove a self-enrollment; omitted/undefined defaults to 'manual' server-side.
  enrollInProgram: (programId: number, data?: {
    userId?: number;
    notes?: string;
    enrollmentSource?: 'onboarding_step' | 'getting_started_card' | 'dismissible_popup' | 'manual';
  }) =>
    api.post<{ data: Enrollment }>(`/training/programs/${programId}/enroll`, data || {}),

  // Phases
  createPhase: (programId: number, data: {
    phaseNumber: number;
    name: string;
    description?: string;
    unlockCriteria?: Record<string, unknown>;
    estimatedDays?: number;
    pointBonus?: number;
  }) => api.post<{ data: Phase }>(`/training/programs/${programId}/phases`, data),
  updatePhase: (phaseId: number, data: Partial<{
    name: string;
    description: string;
    unlockCriteria: Record<string, unknown>;
    estimatedDays: number;
    pointBonus: number;
  }>) => api.patch<{ data: Phase }>(`/training/phases/${phaseId}`, data),
  deletePhase: (phaseId: number) => api.delete<{ message: string }>(`/training/phases/${phaseId}`),

  // Tasks
  createTask: (phaseId: number, data: {
    taskOrder: number;
    title: string;
    description?: string;
    instructions?: string;
    taskType?: 'standard' | 'reflection' | 'skill_practice' | 'quiz' | 'media_submission';
    required?: boolean;
    pointValue?: number;
    estimatedMinutes?: number;
    resources?: string[];
    successCriteria?: string;
  }) => api.post<{ data: Task }>(`/training/phases/${phaseId}/tasks`, data),
  updateTask: (taskId: number, data: Partial<{
    title: string;
    description: string;
    instructions: string;
    taskType: string;
    required: boolean;
    pointValue: number;
    estimatedMinutes: number;
    resources: string[];
    successCriteria: string;
  }>) => api.patch<{ data: Task }>(`/training/tasks/${taskId}`, data),
  deleteTask: (taskId: number) => api.delete<{ message: string }>(`/training/tasks/${taskId}`),

  // My Enrollments
  getMyEnrollments: (params?: { status?: 'active' | 'paused' | 'completed' | 'abandoned'; organizationId?: number }) =>
    api.get<{ data: Enrollment[] }>('/training/enrollments', { params }),
  getEnrollmentProgress: (enrollmentId: number) =>
    api.get<{ data: EnrollmentProgressResponse }>(`/training/enrollments/${enrollmentId}/progress`),
  updateEnrollmentStatus: (enrollmentId: number, status: string, notes?: string) =>
    api.patch<{ data: Enrollment }>(`/training/enrollments/${enrollmentId}`, { status, notes }),
  // Spec 267 Slice 1: accept/decline a pending Dom-initiated enrollment
  // (consent gate). Decline deletes the enrollment row server-side — no
  // record kept, matching the locked mockup copy.
  acceptEnrollment: (enrollmentId: number) =>
    api.post<{ data: Enrollment }>(`/training/enrollments/${enrollmentId}/accept`),
  declineEnrollment: (enrollmentId: number) =>
    api.post<{ message: string }>(`/training/enrollments/${enrollmentId}/decline`),
  // Spec 267 Slice 2: the enrollee leaves an active/paused enrollment
  // unilaterally. `reason` is only ever sent on the "end together"/notify
  // path — the private/solo paths never send it (see ExitProgramModal.tsx).
  exitEnrollment: (enrollmentId: number, data?: { reason?: string; notifyPartner?: boolean }) =>
    api.post<{ data: Enrollment }>(`/training/enrollments/${enrollmentId}/exit`, data || {}),

  // Spec 267 Slice 2: the Dom-facing review queue — every submitted task
  // across every program/enrollee in the org, oldest first.
  getReviewQueue: () => api.get<{ data: ReviewQueueItem[] }>('/training/review-queue'),

  // Task Completion
  startTask: (enrollmentId: number, taskId: number) =>
    api.post<{ data: TaskCompletion }>(`/training/enrollments/${enrollmentId}/tasks/${taskId}/start`),
  // Spec 269: starts a real chastity_locks session for a lock-backed task
  // (verificationKey === 'chastity_lock_completed') instead of the plain
  // in_progress flip startTask does — see trainingProgramsService.ts.
  startChastityLockTask: (enrollmentId: number, taskId: number) =>
    api.post<{ data: { lock: Record<string, unknown>; alreadyStarted: boolean } }>(
      `/training/enrollments/${enrollmentId}/tasks/${taskId}/start-chastity-lock`
    ),
  // Spec 270: links a real protocol (existing or newly-created) to a
  // protocol_adopted task. Mirrors startChastityLockTask's shape — see
  // trainingProgramsService.ts's adoptProtocolForTask for the exact
  // request/response contract.
  adoptProtocol: (
    enrollmentId: number,
    taskId: number,
    input: {
      protocolId?: number;
      newProtocol?: {
        title: string;
        description?: string;
        category_id?: number;
        importance?: 'essential' | 'standard' | 'preferred';
        tracking_type: 'task_based' | 'continuous';
      };
    }
  ) =>
    api.post<{ data: { protocol: Record<string, unknown>; alreadyAdopted: boolean } }>(
      `/training/enrollments/${enrollmentId}/tasks/${taskId}/adopt-protocol`,
      input
    ),
  // Spec 271: starts (or links an existing) negotiation session to a
  // negotiation_started task. Idempotent — a repeat call against an
  // already-linked task returns the same negotiationId with
  // alreadyStarted: true WITHOUT validating partnerId again (see
  // trainingProgramsService.ts's startNegotiationForTask — the existing-link
  // check runs before partnerId is ever used). `partnerId` is the partner's
  // public UUID (same as every other partnerId in this codebase, e.g.
  // limitsAPI.startNegotiation) — the controller resolves it via
  // resolveUserId(), mirroring the sibling POST /api/limits/negotiations
  // route (trainingProgramsController.ts's startNegotiationForTask).
  //
  // The linked negotiationId itself no longer needs recovering via a repeat
  // call after a page reload — getEnrollmentProgress() now durably exposes
  // it as TaskCompletion.linkedNegotiationId (LEFT JOIN on
  // program_task_negotiation_links, trainingProgramsService.ts).
  startNegotiationTask: (enrollmentId: number, taskId: number, partnerId: string) =>
    api.post<{ data: { negotiationId: number; alreadyStarted: boolean } }>(
      `/training/enrollments/${enrollmentId}/tasks/${taskId}/start-negotiation`,
      { partnerId }
    ),
  submitTask: (enrollmentId: number, taskId: number, data: {
    submissionText?: string;
    submissionMediaUrl?: string;
  }) => api.post<{ data: TaskCompletion }>(`/training/enrollments/${enrollmentId}/tasks/${taskId}/submit`, data),
  reviewTask: (enrollmentId: number, taskId: number, approved: boolean, reviewNotes?: string) =>
    api.post<{ data: TaskCompletion }>(`/training/enrollments/${enrollmentId}/tasks/${taskId}/review`, { approved, reviewNotes }),

  // Certificates
  getMyCertificates: () => api.get<{ data: Certificate[] }>('/training/certificates'),
  getCertificate: (enrollmentId: number) =>
    api.get<{ data: Certificate }>(`/training/enrollments/${enrollmentId}/certificate`),
  generateCertificate: (enrollmentId: number) =>
    api.post<{ data: Certificate }>(`/training/enrollments/${enrollmentId}/certificate`),
};
